import { z } from 'zod';
// An organization and its members.
//
// The organization is core data, like a plan: core-api stores it, the console
// and the app (through the storage) manage it. Its members are users the app
// owns, named by their account id — the core holds who belongs where and with
// which role, never who the user is.
// Two roles. An admin manages the organization where the app opens it to self
// service; a member belongs to it.
export const OrganizationRoleSchema = z.enum(['admin', 'member']);
export const OrganizationSchema = z.object({
    id: z.string(),
    name: z.string(),
    createdAt: z.string(),
    updatedAt: z.string()
});
export const OrganizationMemberSchema = z.object({
    userId: z.string(),
    role: OrganizationRoleSchema,
    // The app's identity for the user, when it still knows them.
    name: z.string().optional(),
    email: z.string().optional(),
    // When they joined.
    createdAt: z.string()
});
// --- The console ---
export const AdminOrganizationSchema = OrganizationSchema.extend({
    memberCount: z.number().int().nonnegative()
});
export const AdminOrganizationDetailSchema = OrganizationSchema.extend({
    members: z.array(OrganizationMemberSchema)
});
// An organization a user belongs to, as their page lists it.
export const UserOrganizationSchema = OrganizationSchema.extend({
    role: OrganizationRoleSchema
});
// --- Self-service ---
//
// What a user does with their own organizations, where the app opens it.
// What this user may do in this organization: what the app opens, crossed with
// their role. Computed by the API, so the front offers only what the app and
// the role allow. Leaving follows `members`, and is open to every member. The
// last admin is refused all the same when stepping down or leaving: that
// depends on who else is admin, which the members list says.
export const OrganizationAbilitiesSchema = z.object({
    update: z.boolean(),
    delete: z.boolean(),
    members: z.boolean(),
    // Inviting takes `members`, and the app's email to send.
    invite: z.boolean(),
    roles: z.boolean(),
    leave: z.boolean()
});
export const MyOrganizationSchema = UserOrganizationSchema.extend({
    abilities: OrganizationAbilitiesSchema
});
export const MyOrganizationDetailSchema = MyOrganizationSchema.extend({
    members: z.array(OrganizationMemberSchema)
});
// The user's organizations, and whether the app lets them create one.
export const MyOrganizationsSchema = z.object({
    items: z.array(MyOrganizationSchema),
    canCreate: z.boolean()
});
// A name is shown in listings and settings; past a line it is not a name.
export const ORGANIZATION_NAME_MAX_LENGTH = 200;
export const CreateOrganizationRequestSchema = z.object({
    name: z.string().trim().min(1).max(ORGANIZATION_NAME_MAX_LENGTH)
});
export const UpdateOrganizationRequestSchema = CreateOrganizationRequestSchema;
// The console adds a member by the address of their account, which the app
// resolves: an operator knows people by their email, not by an account id.
export const AddOrganizationMemberRequestSchema = z.object({
    email: z.string().trim().pipe(z.email()),
    role: OrganizationRoleSchema
});
export const UpdateOrganizationMemberRequestSchema = z.object({
    role: OrganizationRoleSchema
});
// --- Invitations ---
//
// In self-service, an admin invites someone by their address, and whoever
// signs in at that address accepts or declines. The invitation waits in the
// app; the email the app sends only says so.
export const InviteToOrganizationRequestSchema = z.object({
    email: z.string().trim().pipe(z.email())
});
// An invitation as the organization's admins see it.
export const OrganizationInvitationSchema = z.object({
    id: z.string(),
    email: z.string(),
    // The member who invited, by account id.
    invitedBy: z.string().optional(),
    createdAt: z.string(),
    expiresAt: z.string()
});
export const OrganizationInvitationsSchema = z.object({
    items: z.array(OrganizationInvitationSchema)
});
// An invitation as the person invited sees it: to what, and from whom.
export const MyInvitationSchema = z.object({
    id: z.string(),
    organization: OrganizationSchema.pick({ id: true, name: true }),
    // The name the app knows for who invited, when it still knows them.
    inviterName: z.string().optional(),
    expiresAt: z.string()
});
export const MyInvitationsSchema = z.object({
    items: z.array(MyInvitationSchema)
});
